import { useEffect, useRef, useState } from 'react'

/**
 * Custom hook for scroll-triggered animations
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Visibility threshold (0-1)
 * @param {string} options.rootMargin - Margin around root
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once
 * @returns {Object} - { ref, isVisible }
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true
  } = options

  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          if (triggerOnce) {
            observer.unobserve(entry.target)
          }
        } else if (!triggerOnce) {
          setIsVisible(false)
        }
      },
      {
        threshold,
        rootMargin
      }
    )

    const currentRef = ref.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [threshold, rootMargin, triggerOnce])

  return { ref, isVisible }
}

/**
 * Hook for staggered animations on multiple elements
 * @param {number} count - Number of elements
 * @param {Object} options - Configuration options
 * @returns {Array} - Array of { ref, isVisible } objects
 */
export const useStaggeredAnimation = (count, options = {}) => {
  const refs = useRef([])
  const [visibleItems, setVisibleItems] = useState(new Set())

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = refs.current.findIndex(ref => ref === entry.target)
          if (entry.isIntersecting) {
            setVisibleItems(prev => new Set([...prev, index]))
            if (options.triggerOnce !== false) {
              observer.unobserve(entry.target)
            }
          } else if (options.triggerOnce === false) {
            setVisibleItems(prev => {
              const newSet = new Set(prev)
              newSet.delete(index)
              return newSet
            })
          }
        })
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '0px 0px -50px 0px'
      }
    )

    refs.current.forEach(ref => {
      if (ref) observer.observe(ref)
    })

    return () => {
      refs.current.forEach(ref => {
        if (ref) observer.unobserve(ref)
      })
    }
  }, [count, options.threshold, options.rootMargin, options.triggerOnce])

  return Array.from({ length: count }, (_, i) => ({
    ref: (el) => { refs.current[i] = el },
    isVisible: visibleItems.has(i)
  }))
}